import React, { useState, useMemo, useCallback } from 'react';
import {
    View, Text, TouchableOpacity, StyleSheet, Image, ScrollView,
} from 'react-native';
import { IActivity, FlashcardReviewConfig, FlashcardCard, KeyStage } from '@eduwrite/shared';
import { useStageTokens } from '../hooks/useStageTokens';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { AudioPromptButton } from './AudioPromptButton';
import { FriendlyButton } from './FriendlyButton';
import { SkiaCanvas } from './SkiaCanvas';
import { useStrokeStore } from '../store/useStrokeStore';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';

interface FlashcardReviewActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * FlashcardReviewActivity — deck/carousel flashcard review with write-back default.
 *
 * Renders ONE card at a time from a shuffled deck. The default interaction is
 * write-back: the student sees the front, handwrites the answer on the canvas,
 * then sees the correct answer for self-comparison. Flip-reveal-only cards
 * (writeBackRequired: false) skip the canvas and use a tap-to-flip interaction.
 *
 * Navigation uses large tap buttons (Next/Previous), NOT swipe gestures
 * (rules/09 anti-pattern: children struggle with swipe).
 *
 * Must live in src/components/ (NOT src/components/activities/ — the stale
 * directory deleted by WP-053).
 */
export const FlashcardReviewActivity: React.FC<FlashcardReviewActivityProps> = ({
    activity,
    onComplete,
    keyStage,
}) => {
    const config = (activity.config || {}) as FlashcardReviewConfig;
    const rawCards: FlashcardCard[] = Array.isArray(config.cards) ? config.cards : [];
    const tokens = useStageTokens(keyStage);
    const completedStrokes = useStrokeStore((state) => state.completedStrokes);
    const setStructuredData = useStrokeStore((state) => state.setStructuredData);
    const clearStrokes = useStrokeStore((state) => state.clearStrokes);

    // Shuffle the deck once on mount (non-seeded random per session)
    const shuffledCards = useMemo(() => {
        const shouldShuffle = config.shuffleOrder !== false;
        if (!shouldShuffle) return rawCards;
        const arr = [...rawCards];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }, [rawCards, config.shuffleOrder]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const [cardResults, setCardResults] = useState<{
        cardId: string;
        correct: boolean;
        mode: 'write_back' | 'flip_reveal';
    }[]>([]);

    // spacedRepetition enabled: true is a no-op in v1 — log a warning, proceed
    useMemo(() => {
        if (config.spacedRepetition?.enabled === true) {
            console.warn('flashcard_review: spacedRepetition.enabled is true but spaced repetition is not implemented in v1. Proceeding with single-pass deck review.');
        }
    }, [config.spacedRepetition]);

    const currentCard = shuffledCards[currentIndex];
    const isLastCard = currentIndex === shuffledCards.length - 1;
    const isFirstCard = currentIndex === 0;

    // Determine if this card requires write-back (default true when unset)
    const isWriteBack = currentCard?.writeBackRequired !== false;

    // Audio playback for card front
    const audioUrl = currentCard?.frontAudioUrl || '';
    const audio = useAudioPlayback(audioUrl);

    // Graceful audio error handling — kind visible message, never silent
    const audioErrorMessage = audio.error
        ? "Let's try again! The audio didn't load."
        : null;

    const titleText = getStudentSafeActivityTitle(activity, 'Flashcard Review');

    // Degenerate deck guard — fewer than 3 cards is not a meaningful review
    if (rawCards.length === 0 || rawCards.length < 3) {
        return (
            <View style={styles.emptyContainer} accessibilityRole="alert" accessibilityLabel="No cards to review yet">
                <Text style={styles.emptyText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    No cards to review yet.
                </Text>
                <Text style={styles.emptySubtext} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    Ask your teacher to add some cards!
                </Text>
            </View>
        );
    }

    const hasStrokes = completedStrokes.length > 0;
    const canShowAnswer = !isWriteBack || hasStrokes;

    const handleShowAnswer = useCallback(() => {
        if (!canShowAnswer) return;
        setShowAnswer(true);
    }, [canShowAnswer]);

    const handleFlip = useCallback(() => {
        setShowAnswer(true);
    }, []);

    const recordCardResult = useCallback((cardId: string, mode: 'write_back' | 'flip_reveal', correct: boolean) => {
        setCardResults(prev => {
            // Replace existing result for this card if re-visited
            const filtered = prev.filter(r => r.cardId !== cardId);
            return [...filtered, { cardId, correct, mode }];
        });
    }, []);

    const handleNext = useCallback(() => {
        // Record the current card's result before advancing
        if (currentCard) {
            const mode: 'write_back' | 'flip_reveal' = isWriteBack ? 'write_back' : 'flip_reveal';
            const correct = isWriteBack ? true : true; // write-back always true (not auto-scored); flip-reveal self-report defaults true
            recordCardResult(currentCard.id, mode, correct);
        }
        // Clear strokes for the next card
        clearStrokes();
        setShowAnswer(false);
        if (currentIndex < shuffledCards.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [currentIndex, shuffledCards.length, currentCard, isWriteBack, recordCardResult, clearStrokes]);

    const handlePrev = useCallback(() => {
        clearStrokes();
        setShowAnswer(false);
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex, clearStrokes]);

    const handleFinish = useCallback(() => {
        // Record the last card's result
        if (currentCard) {
            const mode: 'write_back' | 'flip_reveal' = isWriteBack ? 'write_back' : 'flip_reveal';
            recordCardResult(currentCard.id, mode, true);
        }

        // Build the final cardResults array covering all cards
        const allResults = shuffledCards.map((card) => {
            const existing = cardResults.find(r => r.cardId === card.id);
            if (existing) return existing;
            const mode: 'write_back' | 'flip_reveal' = card.writeBackRequired !== false ? 'write_back' : 'flip_reveal';
            return { cardId: card.id, correct: true, mode };
        });

        // Save FlashcardSessionResult to structuredData before completing
        setStructuredData({ cardResults: allResults });
        onComplete();
    }, [currentCard, isWriteBack, recordCardResult, shuffledCards, cardResults, setStructuredData, onComplete]);

    // Age-adaptive font sizing
    const cardFontSize = tokens.isEYFS ? 28 : tokens.isKS2 ? 20 : 24;

    return (
        <View style={styles.container}>
            {/* Header title */}
            <View style={styles.headerContainer}>
                <Text style={[styles.headerTitle, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    {titleText}
                </Text>
            </View>

            {/* Progress indicator */}
            <View style={styles.progressContainer}>
                <Text style={styles.progressText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    Card {currentIndex + 1} of {shuffledCards.length}
                </Text>
            </View>

            {/* Card content */}
            <ScrollView
                style={styles.cardContainer}
                contentContainerStyle={styles.cardContent}
                showsVerticalScrollIndicator={false}
            >
                {/* Front of card */}
                <View style={styles.cardFront}>
                    {/* Optional front image */}
                    {currentCard.frontImageUrl ? (
                        <Image
                            testID={`flashcard-front-image-${currentCard.id}`}
                            source={{ uri: currentCard.frontImageUrl }}
                            style={[styles.cardImage, { height: tokens.imageHeight }]}
                            resizeMode="contain"
                            accessibilityLabel="Card picture"
                        />
                    ) : null}

                    {/* Front text (prompt) */}
                    <Text
                        testID={`flashcard-front-text-${currentCard.id}`}
                        style={[styles.cardFrontText, { fontSize: cardFontSize, lineHeight: cardFontSize * 1.4 }]}
                        allowFontScaling={true}
                        maxFontSizeMultiplier={1.5}
                    >
                        {currentCard.front}
                    </Text>

                    {/* Audio play button (via WP-055 helper) */}
                    {audioUrl ? (
                        <View style={styles.audioContainer}>
                            <AudioPromptButton
                                text="Listen"
                                autoPlay={false}
                            />
                            {audioErrorMessage && (
                                <Text
                                    testID={`flashcard-audio-error-${currentCard.id}`}
                                    style={styles.audioErrorText}
                                    allowFontScaling={true}
                                    maxFontSizeMultiplier={1.5}
                                >
                                    {audioErrorMessage}
                                </Text>
                            )}
                        </View>
                    ) : null}
                </View>

                {/* Write-back canvas (only for write-back cards, hidden after answer shown) */}
                {isWriteBack && !showAnswer && (
                    <View style={styles.canvasContainer}>
                        <SkiaCanvas />
                    </View>
                )}

                {/* Back of card (revealed after Show Answer / Flip) */}
                {showAnswer && (
                    <View style={styles.cardBack}>
                        {currentCard.backImageUrl ? (
                            <Image
                                testID={`flashcard-back-image-${currentCard.id}`}
                                source={{ uri: currentCard.backImageUrl }}
                                style={[styles.cardImage, { height: tokens.imageHeight }]}
                                resizeMode="contain"
                                accessibilityLabel="Answer picture"
                            />
                        ) : null}
                        <Text
                            testID={`flashcard-back-text-${currentCard.id}`}
                            style={[styles.cardBackText, { fontSize: cardFontSize, lineHeight: cardFontSize * 1.4 }]}
                            allowFontScaling={true}
                            maxFontSizeMultiplier={1.5}
                        >
                            {currentCard.back}
                        </Text>
                    </View>
                )}
            </ScrollView>

            {/* Action area: Show Answer / Flip button */}
            {!showAnswer && (
                <View style={styles.actionContainer}>
                    {isWriteBack ? (
                        <TouchableOpacity
                            testID="flashcard-show-answer-btn"
                            onPress={handleShowAnswer}
                            disabled={!canShowAnswer}
                            style={[
                                styles.actionButton,
                                styles.showAnswerButton,
                                { minHeight: tokens.touchTargetMin, minWidth: tokens.touchTargetMin },
                                !canShowAnswer && styles.disabledButton,
                            ]}
                            activeOpacity={0.75}
                            accessibilityLabel="Show answer"
                            accessibilityRole="button"
                            accessibilityState={{ disabled: !canShowAnswer }}
                        >
                            <Text style={[styles.actionButtonText, !canShowAnswer && styles.disabledButtonText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                Show Answer
                            </Text>
                        </TouchableOpacity>
                    ) : (
                        <TouchableOpacity
                            testID="flashcard-flip-btn"
                            onPress={handleFlip}
                            style={[
                                styles.actionButton,
                                styles.flipButton,
                                { minHeight: tokens.touchTargetMin, minWidth: tokens.touchTargetMin },
                            ]}
                            activeOpacity={0.75}
                            accessibilityLabel="Flip card"
                            accessibilityRole="button"
                        >
                            <Text style={styles.actionButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                Flip
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}

            {/* Navigation controls */}
            <View style={styles.navContainer}>
                {/* Previous button — hidden on first card */}
                {!isFirstCard ? (
                    <TouchableOpacity
                        testID="flashcard-prev-btn"
                        onPress={handlePrev}
                        style={[
                            styles.navButton,
                            styles.prevButton,
                            { minHeight: tokens.touchTargetMin, minWidth: tokens.touchTargetMin },
                        ]}
                        activeOpacity={0.75}
                        accessibilityLabel="Previous card"
                        accessibilityRole="button"
                    >
                        <Text style={styles.navButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            ← Back
                        </Text>
                    </TouchableOpacity>
                ) : (
                    <View style={styles.navButtonPlaceholder} />
                )}

                {/* Next or Finish button */}
                {isLastCard ? (
                    <FriendlyButton
                        variant="primary"
                        size="large"
                        onPress={handleFinish}
                        testID="flashcard-finish-btn"
                        accessibilityLabel="Finish review"
                    >
                        FINISH ✓
                    </FriendlyButton>
                ) : (
                    <TouchableOpacity
                        testID="flashcard-next-btn"
                        onPress={handleNext}
                        style={[
                            styles.navButton,
                            styles.nextButton,
                            { minHeight: tokens.touchTargetMin, minWidth: tokens.touchTargetMin },
                        ]}
                        activeOpacity={0.75}
                        accessibilityLabel="Next card"
                        accessibilityRole="button"
                    >
                        <Text style={styles.navButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            Next →
                        </Text>
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    headerContainer: {
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8,
        alignItems: 'center',
    },
    headerTitle: {
        fontWeight: '900',
        color: '#1E293B',
        textAlign: 'center',
    },
    progressContainer: {
        alignItems: 'center',
        paddingVertical: 8,
    },
    progressText: {
        fontSize: 14,
        color: '#64748B',
        fontWeight: '600',
    },
    cardContainer: {
        flex: 1,
    },
    cardContent: {
        paddingHorizontal: 24,
        paddingVertical: 16,
        flexGrow: 1,
    },
    cardFront: {
        alignItems: 'center',
        marginBottom: 12,
    },
    cardImage: {
        width: '100%',
        borderRadius: 16,
        marginBottom: 16,
    },
    cardFrontText: {
        color: '#1E293B',
        fontWeight: '700',
        textAlign: 'center',
        marginBottom: 12,
    },
    cardBack: {
        alignItems: 'center',
        marginTop: 16,
        padding: 16,
        backgroundColor: '#F0FDF4',
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#86EFAC',
    },
    cardBackText: {
        color: '#15803D',
        fontWeight: '800',
        textAlign: 'center',
    },
    canvasContainer: {
        flex: 1,
        minHeight: 200,
        borderWidth: 2,
        borderColor: '#BFDBFE',
        borderRadius: 16,
        marginBottom: 12,
    },
    audioContainer: {
        alignItems: 'center',
        marginTop: 12,
    },
    audioErrorText: {
        fontSize: 14,
        color: '#FAAD14',
        fontWeight: '600',
        marginTop: 8,
        textAlign: 'center',
    },
    actionContainer: {
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingBottom: 8,
    },
    actionButton: {
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 32,
    },
    showAnswerButton: {
        backgroundColor: '#1890FF',
    },
    flipButton: {
        backgroundColor: '#722ED1',
    },
    disabledButton: {
        backgroundColor: '#E2E8F0',
    },
    actionButtonText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    disabledButtonText: {
        color: '#94A3B8',
    },
    navContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderTopWidth: 1.5,
        borderTopColor: '#BFDBFE',
    },
    navButton: {
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    prevButton: {
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        borderColor: '#CBD5E1',
    },
    nextButton: {
        backgroundColor: '#1890FF',
    },
    navButtonText: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    navButtonPlaceholder: {
        minWidth: 56,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#FFFBEB',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#FCD34D',
        margin: 16,
    },
    emptyText: {
        fontSize: 20,
        color: '#92400E',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 8,
    },
    emptySubtext: {
        fontSize: 16,
        color: '#B45309',
        fontWeight: '600',
        textAlign: 'center',
    },
});
